"use client";

import React, { useEffect, useState } from "react";
import {
  Settings,
  Languages,
  BarChart3,
  Cog,
  ChevronRight,
  ArrowLeft,
  Plug,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/shadcn-components/ui/sheet";
import { useSettingsStore, LANGUAGES } from "@/lib/settings";
import { APP_DISPLAY_VERSION, APP_FOOTER_BUILD_LABEL } from "@/lib/app-version";
import { usePlatform } from "@/platform/PlatformProvider";
import type { McpBridgeStatus } from "@/platform/types";
import { ChartsSettingsContent } from "./ChartsSettingsContent";
import { LanguagesContent } from "./LanguagesContent";
import { McpServerContent } from "./McpServerContent";
import { SettingsDetailsContent } from "./SettingsDetailsContent";

interface SettingsSheetProps {
  trigger: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SettingItem {
  id: string;
  icon: React.ElementType;
  iconBg: string;
  title: string;
  subtitle?: string;
  hasArrow: boolean;
}

type SettingsView = "main" | "interface" | "charts" | "settings" | "mcp";

export function SettingsSheet({
  trigger,
  isOpen,
  onOpenChange,
}: SettingsSheetProps) {
  const [currentView, setCurrentView] = useState<SettingsView>("main");
  const { getSetting } = useSettingsStore();
  // Desktop-only capability. Its absence on web/iOS is what hides the row —
  // never branch on platform.platform here.
  const mcpBridge = usePlatform().mcp;
  const [mcpStatus, setMcpStatus] = useState<McpBridgeStatus | null>(null);

  useEffect(() => {
    if (!mcpBridge) return;
    const off = mcpBridge.onStatusChange(setMcpStatus);
    void mcpBridge.getStatus().then(setMcpStatus).catch(() => undefined);
    return off;
  }, [mcpBridge]);

  // Get the display name for the selected language
  const getLanguageDisplayName = () => {
    const languageId = getSetting("language");
    const language = LANGUAGES.find((lang) => lang.id === languageId);
    return language ? language.name : "English";
  };

  const settingItems: SettingItem[] = [
    {
      id: "interface",
      icon: Languages,
      iconBg: "bg-blue-500",
      title: "Interface",
      subtitle: getLanguageDisplayName(),
      hasArrow: true,
    },
    {
      id: "charts",
      icon: BarChart3,
      iconBg: "bg-blue-600",
      title: "Charts",
      hasArrow: true,
    },
    {
      id: "settings",
      icon: Cog,
      iconBg: "bg-gray-600",
      title: "Settings",
      hasArrow: true,
    },
  ];

  // Fourth row — desktop builds only, where the MCP bridge adapter exists.
  if (mcpBridge) {
    settingItems.push({
      id: "mcp",
      icon: Plug,
      iconBg: "bg-blue-500",
      title: "MCP Server",
      subtitle: mcpStatus?.running
        ? `listening · 127.0.0.1:${mcpStatus.port}`
        : "not running",
      hasArrow: true,
    });
  }

  const handleItemClick = (itemId: string) => {
    setCurrentView(itemId as SettingsView);
  };

  const handleBackToMain = () => {
    setCurrentView("main");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCurrentView("main");
    }
    onOpenChange(open);
  };

  const renderMainView = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Settings</h2>
          <p className="text-sm text-muted-foreground">
            Manage your preferences and configurations
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-3">
          <Card>
            <CardContent className="p-0">
              {settingItems.map((item, index) => (
                <div key={item.id}>
                  <Button
                    variant="ghost"
                    onClick={() => handleItemClick(item.id)}
                    className={`w-full h-auto p-3 justify-start hover:bg-muted/50 rounded-none
                      ${index === 0 ? "rounded-t-lg" : ""}
                      ${index === settingItems.length - 1 ? "rounded-b-lg" : ""}
                    `}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg ${item.iconBg} flex items-center justify-center mr-3 flex-shrink-0`}
                    >
                      <item.icon className="text-white text-base" />
                    </div>
                    <div className="flex-1 text-left min-w-0 mr-2">
                      <div className="text-sm font-medium">{item.title}</div>
                      {item.subtitle && (
                        <div className="text-xs text-muted-foreground truncate">
                          {item.subtitle}
                        </div>
                      )}
                    </div>
                    {item.hasArrow && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </Button>
                  {index < settingItems.length - 1 && (
                    <div className="h-px bg-border mx-3" />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-border">
        <div className="text-xs text-muted-foreground text-center">
          VybeTrader • {APP_DISPLAY_VERSION} • {APP_FOOTER_BUILD_LABEL}
        </div>
      </div>
    </div>
  );

  const renderCurrentView = () => {
    switch (currentView) {
      case "interface":
        return <LanguagesContent onBack={handleBackToMain} />;
      case "charts":
        return <ChartsSettingsContent onBack={handleBackToMain} />;
      case "settings":
        return <SettingsDetailsContent onBack={handleBackToMain} />;
      case "mcp":
        return <McpServerContent onBack={handleBackToMain} />;
      default:
        return renderMainView();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>

      <SheetContent
        side="right"
        className="w-[400px] sm:w-[540px] flex flex-col overflow-hidden p-0"
      >
        <div className="h-full relative overflow-hidden">
          {/* Main View */}
          <div
            className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
              currentView === "main" ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            {renderMainView()}
          </div>

          {/* Sub Views */}
          <div
            className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
              currentView !== "main" ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {renderCurrentView()}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
